import { Company } from 'module/api/company/types';
import { QueryCombinerType, SimpleSearchFilter } from 'module/api/filter/types';
import { SourceType } from 'module/api/filter/types';
import { fieldToFilterableField } from 'module/helpers/filter/type-transformers';
import {
  formatShortDateInTimezone,
  getCurrentUserTz,
} from 'module/helpers/timezone';
import { useTranslation } from 'module/hooks/use-translation';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { useDeleteConfirmModal } from 'module/ui/components/atoms/modal/use-delete-confirm-modal';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Popover } from 'module/ui/components/atoms/popover';
import { FilterBar } from 'module/ui/components/molecules/filter-bar';
import { HelperText01, Short01 } from 'module/ui/components/styles';
import { faEnvelope } from '@fortawesome/pro-duotone-svg-icons';
import {
  faAnglesUpDown,
  faArrowsToDottedLine,
  faEdit,
  faSidebar,
  faPage,
  faEnvelope as faEnvelopeRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faEllipsisVertical,
  faSignal,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useParams } from '@rocketlane/rocket-router';
import {
  Button,
  EmptyState as RocketlaneEmptyState,
  Icon,
} from '@rocketlane/ui';
import { Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { EmailThreadStatus } from 'api/email/types';
import { PageType } from 'api/page-template/types';
import { useSignalFieldsAsFilterableFieldWithReason } from 'helpers/fields/signal-fields/fields';
import moment from 'moment';
import { useCurrentUserQuery } from 'queries/account';
import { useEmailMessagesQuery } from 'queries/email-messages';
import {
  useDeleteEmailThreadMutation,
  useEmailThreadStatusQuery,
  useSearchEmailsScrollPagination,
} from 'queries/emails';
import { useGetAllEmailFieldsQuery } from 'queries/fields';
import { useDeletePage } from 'queries/pages/use-delete-page';
import { useEmailTimelineQuery } from 'queries/signal-occurrences';
import React from 'react';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { MetadataTooltip } from 'components/common/metadata-tooltip';
import { ResizableSidebar } from 'components/organisms/pages/shared/resizable-sidebar';
import {
  LoadingWrapper,
  Sidebar,
  SidebarInPopover,
  PageWrapper,
  PageContent,
  MainContent,
  SecondaryFilter,
  SidebarMenu,
} from 'components/organisms/pages/shared/styles';
import { EmailSignalsSidebar } from './email-signals-sidebar';
import { useEmailPagesRouteNavigations } from './route-navigations';
import { useCurrentEmailPage } from './use-current-email-page';
import { useEmailPagesList } from './use-email-pages-list';
import { ChatProvider, useChatContext } from '../chat/chat-context';
import { ChatSidebar } from '../chat/chat-sidebar';
import { useChatTrigger } from '../chat/use-chat-trigger';
import { Document } from '../document/document';
import { BlockedContactBanner } from '../email-transcript/blocked-contact-banner';
import {
  ContentReadyOverlay,
  HiddenUntilReady,
} from '../email-transcript/content-ready-overlay';
import { RenderEmailAttachment } from '../email-transcript/email-attachment';
import {
  EmailSummaryCard,
  useEmailSummary,
} from '../email-transcript/email-summary-card';
import { EmailThreadNotFoundState } from '../email-transcript/email-thread-not-found';
import {
  EmailTranscript,
  EmailTranscriptHandle,
} from '../email-transcript/email-transcript';
import { MeetingPagesSidePane } from '../meeting-pages/side-pane';

const RocketlaneEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: var(--spacing-09) var(--spacing-08);
`;

const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const EmailSidebarMenu = styled(SidebarMenu)`
  .ant-menu-item {
    height: auto !important;
    line-height: normal !important;
    padding-top: var(--spacing-03) !important;
    padding-bottom: var(--spacing-03) !important;
    border-radius: var(--border-radius-small) !important;
  }
  .ant-menu-item-selected {
    border-radius: var(--border-radius-small) !important;
  }
`;

// v2-only sidebar containers: disable outer scroll/padding so the inner
// flex layout controls both the fixed search header and the scrollable list.
const EmailV2Sidebar = styled(ResizableSidebar)`
  padding: 0;
  overflow: hidden;
  overflow-y: hidden;
`;

const EmailV2SidebarInPopover = styled(SidebarInPopover)`
  padding: 0 !important;
  overflow: hidden !important;
  overflow-y: hidden !important;
`;

const SidebarContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SidebarSearchSection = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-05) var(--spacing-05) var(--spacing-03);
  background: var(--white-100);
`;

const SidebarSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  min-width: 0;
`;

const SearchInputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const FilterLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  flex-shrink: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;

const SidebarEmailList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 var(--spacing-05) var(--spacing-05);
`;

const BannerCenterWrapper = styled.div`
  padding: 0 0 var(--spacing-05);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -16px;
    height: 16px;
    background: linear-gradient(
      to bottom,
      var(--white-100, #fff) 0%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const BannerCenterInner = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const EmailActionBarTitleWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
  overflow: hidden;
  margin-right: var(--spacing-04);
`;

/* FilterBar.Primary and FilterBar.Secondary both default to flex:1, splitting
   the bar 50/50 regardless of content. Secondary only holds a compact button
   group, so pin it to its content width and let Primary's title fill the rest.
   Primary also needs min-width:0 — without it, a flex item's default
   min-width:auto keeps it from shrinking past its content's intrinsic width,
   which is exactly what the title needs to do to ellipsis instead of overflowing. */
const EmailFilterBarLayout = styled.div`
  width: 100%;

  ${FilterBar.Primary} {
    min-width: 0;
  }

  ${FilterBar.Secondary} {
    flex: 0 0 auto;
  }
`;

const EmailActionBarTitleInner = styled.div<{ $isPlaceholder?: boolean }>`
  width: 100%;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: ${({ $isPlaceholder }) =>
    $isPlaceholder ? 'var(--text-secondary)' : 'var(--text-primary)'};
  font-style: ${({ $isPlaceholder }) => ($isPlaceholder ? 'italic' : 'normal')};
  overflow-x: hidden;
  overflow-y: visible;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FilterBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

enum EmailPagesRightSidebarKeys {
  Signals = 'signals',
}

const EmailListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow: visible;
  justify-content: center;
  gap: var(--spacing-01);
`;
const EmailListItemHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--spacing-02);
  width: 100%;
  min-width: 0;
`;
const EmailListItemTitle = styled.div`
  ${Short01};
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EmailListItemDate = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
  flex-shrink: 0;
  white-space: nowrap;
`;
const EmailListItemPreview = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Number of threads fetched on the initial load.
const EMAILS_INITIAL_PAGE_SIZE = 25;
// Number of threads fetched on each subsequent scroll page.
const EMAILS_PAGE_SIZE = 10;
// Distance (px) from the list bottom at which we prefetch the next page.
const INFINITE_SCROLL_THRESHOLD = 200;

export type EmailPagesPermissions = {
  canReadEmailPages: boolean;
  canWriteEmailPages?: boolean;
  canDeleteEmailPages: boolean;
  canReadEmailPageTemplates: boolean;
  canCreateEmailPageTemplates: boolean;
  canWriteEmailPageTemplates: boolean;
  canDeleteEmailPageTemplates: boolean;
  canWriteEmailIntelligenceSettings?: boolean;
};

interface AccountEmailsProps {
  company: Company;
  projectId?: number;
  emailPermissions?: EmailPagesPermissions;
  isModuleFed?: boolean;
  renderAttachment?: RenderEmailAttachment;
  hostApi?: {
    renderComponent: (request: {
      type: string;
      props: Record<string, any>;
    }) => React.ReactNode;
    hasFeatures?: (feature: string) => boolean;
    searchText?: string;
    onSearchTextChange?: (text: string) => void;
  };
  filterPayload?: SimpleSearchFilter;
  onClearFilter?: () => void;
  emailFields?: unknown[];
  emailMessageId?: number;
}

const AccountEmailsContentV1 = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
}: AccountEmailsProps) => {
  const intl = useIntl();
  const { value, onChange, clear } = useSearchValueState();
  const { emailPages, isEmailPagesLoading, allEmailPages } = useEmailPagesList({
    company,
    searchText: value,
  });
  const { emailPage, isEmailPageLoading } = useCurrentEmailPage({
    company,
    emailPages,
    isEmailPagesLoading,
    projectId,
  });

  const { navigateToList, navigateToEmail, navigateToEmailEdit } =
    useEmailPagesRouteNavigations(projectId);

  const emailPageDocumentMappingId =
    emailPage?.metadata.documentSourceMappingModel?.pageDocumentMappingId;
  const emailDocumentId = emailPage?.metadata.documentId;

  // Use allEmailPages (full dataset), not emailPages (search-filtered).
  // With active search, emailPages can be empty despite other pages existing,
  // which would incorrectly trigger navigateToList and drop context.
  const allEmailAssociations = useMemo(
    () => allEmailPages.flatMap((page) => page.associations),
    [allEmailPages],
  );

  const { handleDelete } = useDeletePage({
    pageDocumentMappingId: emailPageDocumentMappingId,
    documentId: emailDocumentId,
    pageType: PageType.Email,
    companyId: projectId ?? company.companyId,
    allPages: allEmailAssociations,
    navigateToPage: navigateToEmail,
    navigateToList: navigateToList,
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { isChatOpen, setIsChatOpen, chatData, setChatData } = useChatContext();

  useChatTrigger();

  const handleChatDataUsed = useCallback(() => {
    if (chatData) {
      setChatData({
        ...chatData,
        elementContext: '',
      });
    }
  }, [setChatData, chatData]);

  const sidebarItems = useMemo((): ItemType[] => {
    if (!emailPages?.length) {
      return [];
    }

    return emailPages.flatMap((page) =>
      page.associations.map((association) => ({
        label: (
          <EmailListItemContent>
            <EmailListItemTitle>{association.title}</EmailListItemTitle>
            <EmailListItemDate>
              {association.updatedAt
                ? humanise(moment(association.updatedAt))
                : ''}
            </EmailListItemDate>
          </EmailListItemContent>
        ),
        key: String(association.documentId),
        icon: <FontAwesomeIcon icon={faPage} size={12} />,
      })),
    );
  }, [emailPages]);

  const selectedAssociationKey =
    emailPage?.metadata?.documentId?.toString() || '';

  React.useEffect(() => {
    setIsChatOpen(false);
  }, [selectedAssociationKey, setIsChatOpen]);

  const searchPlaceholder = useTranslation('search');
  const SideBar = useMemo(() => {
    return (
      <>
        <div style={{ marginBottom: 'var(--spacing-03)' }}>
          <SearchInputExpanded
            placeholder={searchPlaceholder}
            value={value}
            onChange={onChange}
            onClear={clear}
          />
        </div>
        <div className="flex flex-col">
          <SidebarMenu
            items={sidebarItems}
            onSelect={({ key }) => {
              const companyIdForNavigation = projectId ?? company.companyId;

              navigateToEmail(companyIdForNavigation, Number(key));
            }}
            selectedKeys={[selectedAssociationKey]}
            mode="inline"
          />
        </div>
      </>
    );
  }, [
    searchPlaceholder,
    value,
    onChange,
    clear,
    sidebarItems,
    selectedAssociationKey,
    navigateToEmail,
    company.companyId,
    projectId,
  ]);

  if (isEmailPagesLoading) {
    return <PageLoading />;
  }

  return (
    <PageWrapper>
      {isSidebarOpen && <Sidebar>{SideBar}</Sidebar>}

      <PageContent>
        <MainContent>
          <FilterBar>
            <FilterBar.Primary>
              {isEmailPagesLoading || allEmailPages?.length === 0 ? null : (
                <Popover
                  content={
                    isSidebarOpen ? null : (
                      <SidebarInPopover>{SideBar}</SidebarInPopover>
                    )
                  }
                  placement="bottomLeft"
                  destroyTooltipOnHide
                  isDisabled={isSidebarOpen}
                  mouseEnterDelay={0.3}
                >
                  <Button
                    variant="ghost"
                    leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  />
                </Popover>
              )}
            </FilterBar.Primary>
            <SecondaryFilter>
              {emailPage != null && !isEmailPageLoading && (
                <>
                  <MetadataTooltip
                    updatedAt={emailPage.metadata.updatedAt}
                    createdAt={emailPage.metadata.createdAt}
                  />

                  {(!isModuleFed || emailPermissions?.canWriteEmailPages) && (
                    <Button
                      variant="primary"
                      leftIcon={<FontAwesomeIcon icon={faEdit} size={12} />}
                      onClick={() => {
                        const companyIdForNavigation =
                          projectId ?? company.companyId;

                        navigateToEmailEdit(
                          companyIdForNavigation,
                          emailPage.metadata.documentId,
                        );
                      }}
                    >
                      <FormattedMessage id="edit" />
                    </Button>
                  )}

                  {(!isModuleFed || emailPermissions?.canDeleteEmailPages) && (
                    <MoreActionsDropdown
                      menu={{
                        onClick: ({ key }) => {
                          if (key === 'delete') {
                            handleDelete();
                          }
                        },
                        items: [
                          {
                            type: 'group',
                            label: <FormattedMessage id="more_actions" />,
                            children: [
                              {
                                key: 'delete',
                                label: <FormattedMessage id="delete" />,
                                icon: <FontAwesomeIcon icon={faTrash} />,
                                danger: true,
                                disabled: !emailPageDocumentMappingId,
                              },
                            ],
                          },
                        ],
                      }}
                      triggerElement={
                        <Button
                          variant="ghost"
                          leftIcon={
                            <FontAwesomeIcon
                              icon={faEllipsisVertical}
                              size={12}
                            />
                          }
                        />
                      }
                    />
                  )}
                </>
              )}
            </SecondaryFilter>
          </FilterBar>

          {isEmailPageLoading && allEmailPages?.length > 0 ? (
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          ) : emailPage == null ? (
            <RocketlaneEmptyStateRoot>
              <RocketlaneEmptyState
                icon={{ icon: faEnvelope }}
                message={intl.formatMessage({
                  id: 'email_page.empty_state_title',
                })}
                description={intl.formatMessage({
                  id: 'email_page.empty_state_message',
                })}
              />
            </RocketlaneEmptyStateRoot>
          ) : (
            <Document
              isChatOpen={isChatOpen}
              document={emailPage}
              readonly={true}
              pageType={PageType.Email}
              sourceId={
                emailPage?.metadata?.documentSourceMappingModel?.sourceId
              }
            />
          )}
        </MainContent>
      </PageContent>

      <ChatSidebar
        isOpen={isChatOpen}
        title={emailPage?.metadata.title}
        accountId={company.companyId}
        userId={303}
        chatData={chatData}
        onChatDataUsed={handleChatDataUsed}
        onClose={() => setIsChatOpen(false)}
      />
    </PageWrapper>
  );
};

const AccountEmailsContentV2 = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
  renderAttachment,
  hostApi,
  filterPayload,
  onClearFilter,
  emailMessageId,
}: AccountEmailsProps) => {
  const intl = useIntl();
  const searchText = hostApi?.searchText ?? '';
  const onSearchTextChange = hostApi?.onSearchTextChange;

  const { data: currentUser } = useCurrentUserQuery();
  const userTimeZone = currentUser?.user.timeZone || getCurrentUserTz();

  const { fields: signalFields } = useSignalFieldsAsFilterableFieldWithReason();

  const { data: rawEmailFields = [] } = useGetAllEmailFieldsQuery();
  const emailFields = useMemo(
    () =>
      rawEmailFields.map((f) => fieldToFilterableField(f, SourceType.Email)),
    [rawEmailFields],
  );

  const activeFilter: SimpleSearchFilter = filterPayload ?? {
    match: QueryCombinerType.All,
  };

  const {
    emails,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSearchEmailsScrollPagination(activeFilter, {
    enabled: true,
    limit: EMAILS_PAGE_SIZE,
    initialLimit: EMAILS_INITIAL_PAGE_SIZE,
    companyId: company.companyId,
  });

  // After the first successful load, never show the full-page loader again —
  // filter refetches show inline loaders in the sidebar and content area instead.
  const initialLoadComplete = useRef(false);
  // Ref on the sidebar scroll container — used to detect when the initial
  // page doesn't fill the visible area so we can prefetch the next page.
  const sidebarRef = useRef<HTMLDivElement>(null);

  if (!initialLoadComplete.current && !isLoading) {
    initialLoadComplete.current = true;
  }

  const { navigateToEmail, navigateToList } = useEmailPagesRouteNavigations(
    projectId,
    true,
  );

  const { emailId: emailIdParam, emailPageId: emailPageIdParam } = useParams<{
    emailId?: string;
    emailPageId?: string;
  }>();
  const selectedThreadIdParam = emailIdParam ?? emailPageIdParam;
  const selectedThreadId =
    selectedThreadIdParam != null ? Number(selectedThreadIdParam) : NaN;
  const isThreadSelected = !Number.isNaN(selectedThreadId);

  const sourceIdForTranscript = isThreadSelected ? selectedThreadId : undefined;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { setIsChatOpen } = useChatContext();

  useChatTrigger();

  // Fetch signals for the selected email thread
  const { data: emailTimeline, isLoading: isSignalsLoading } =
    useEmailTimelineQuery(company.companyId, sourceIdForTranscript);

  const { summaryPoints, isSummaryLoading, hasSummary } = useEmailSummary(
    sourceIdForTranscript,
  );

  const { data: emailThreadStatus } = useEmailThreadStatusQuery(
    sourceIdForTranscript,
  );
  const isBlockedThread =
    emailThreadStatus?.status === EmailThreadStatus.Blocked;

  // Cache-shared with EmailTranscript's own fetch (same query key) — used
  // here only to help detect a thread that genuinely doesn't exist (e.g. a
  // stale/bookmarked URL to a deleted thread), not to duplicate the request.
  const { data: transcriptMessages, isLoading: isMessagesLoading } =
    useEmailMessagesQuery(sourceIdForTranscript);

  const signalCount = emailTimeline?.signalOccurrences?.length ?? 0;
  const showSummaryBanner =
    !isSignalsLoading &&
    !isSummaryLoading &&
    sourceIdForTranscript != null &&
    hasSummary;

  const transcriptRef = useRef<EmailTranscriptHandle>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  useEffect(() => {
    setIsAllExpanded(false);
  }, [sourceIdForTranscript]);

  // Mirrors the email-thread drawer: hold the transcript area's loader until
  // the visible messages' iframes and the summary have both settled, so
  // everything appears together instead of popping in piecemeal.
  const [isTranscriptContentReady, setIsTranscriptContentReady] =
    useState(false);

  useEffect(() => {
    setIsTranscriptContentReady(false);
  }, [sourceIdForTranscript]);

  const isTranscriptReady = isTranscriptContentReady && !isSummaryLoading;

  // Right side pane
  const [activeRightSidebarKey, setActiveRightSidebarKey] =
    useState<EmailPagesRightSidebarKeys>();
  const [retainedSubject, setRetainedSubject] = useState<string | undefined>();
  const [isListPopoverOpen, setIsListPopoverOpen] = useState(false);

  const openRightSideBar = useCallback((key: EmailPagesRightSidebarKeys) => {
    setActiveRightSidebarKey(key);
    setIsSidebarOpen(false);
    setIsListPopoverOpen(false);
  }, []);

  const handleCloseRightSidebar = useCallback(() => {
    setActiveRightSidebarKey(undefined);
  }, []);

  const selectedAssociationKey = isThreadSelected
    ? String(selectedThreadId)
    : '';

  const selectedThread = useMemo(
    () => emails.find((email) => email.emailThreadId === selectedThreadId),
    [emails, selectedThreadId],
  );

  // The per-thread message fetch is the authoritative source for "does this
  // thread exist" — mirrors the email-thread drawer's simpler check, rather
  // than also waiting on the paginated sidebar list to fully load first. An
  // initial fetch failure already leaves `transcriptMessages` empty/undefined,
  // so the length check alone covers that; checking `isError` on its own would
  // also fire on a background refetch failure *after* a successful load,
  // hiding an already-visible thread behind the not-found state.
  const isThreadNotFound =
    isThreadSelected &&
    !isMessagesLoading &&
    (transcriptMessages?.length ?? 0) === 0;

  // When the selected thread changes, close chat, reset the side pane, and close list popover.
  useEffect(() => {
    setIsChatOpen(false);
    setActiveRightSidebarKey(undefined);
    setRetainedSubject(undefined);
    setIsListPopoverOpen(false);
  }, [selectedAssociationKey, setIsChatOpen]);

  // Persist the subject so it survives filter-induced gaps where selectedThread
  // drops out of the filtered list and emailTimeline hasn't loaded yet.
  const resolvedSubject =
    selectedThread?.subject || emailTimeline?.source?.title;

  useEffect(() => {
    if (resolvedSubject) {
      setRetainedSubject(resolvedSubject);
    }
  }, [resolvedSubject]);

  const { mutate: deleteEmailThreadMutate } = useDeleteEmailThreadMutation();

  const { open: confirmDeleteThread, dialog: deleteConfirmationDialog } =
    useDeleteConfirmModal({
      title: intl.formatMessage({
        id: 'account.email_pages.delete_thread.confirm.title',
        defaultMessage: 'Delete email thread?',
      }),
      content: intl.formatMessage({
        id: 'account.email_pages.delete_thread.confirm.message',
        defaultMessage:
          'Deleting this email thread will remove the signals associated with it and cannot be restored. The attachments added to the project will be unaffected. Are you sure you want to delete?',
      }),
      onOk: () => {
        if (Number.isNaN(selectedThreadId)) {
          return;
        }

        deleteEmailThreadMutate(selectedThreadId, {
          onSuccess: () => {
            const remainingEmails = emails.filter(
              (email) => email.emailThreadId !== selectedThreadId,
            );

            if (remainingEmails.length > 0) {
              navigateToEmail(
                projectId ?? company.companyId,
                remainingEmails[0].emailThreadId,
              );
            } else {
              navigateToList(projectId ?? company.companyId);
            }
          },
        });
      },
    });

  const noSubjectLabel = intl.formatMessage({
    id: 'account.email_pages.no_subject',
    defaultMessage: '(No subject)',
  });
  // When the current thread is loaded with no subject, show "(No subject)"
  // immediately rather than letting a stale retainedSubject from the previous
  // thread appear in the action bar title.
  const displaySubject =
    resolvedSubject ||
    (selectedThread && !selectedThread.subject
      ? noSubjectLabel
      : retainedSubject);
  // Placeholder styling only when the loaded thread genuinely has no subject.
  // A filter-induced gap (selectedThread=undefined, retainedSubject showing) is
  // not a placeholder — the retained text is a real subject from a prior thread.
  const isPlaceholderSubject =
    !resolvedSubject && Boolean(selectedThread && !selectedThread.subject);

  const rightSidebarItems = useMemo(
    () => [
      {
        key: EmailPagesRightSidebarKeys.Signals,
        title:
          signalCount > 0
            ? intl.formatMessage(
                {
                  id: 'account.email_pages.signals_tab_title_with_count',
                  defaultMessage: 'Signals ({count})',
                },
                { count: signalCount },
              )
            : intl.formatMessage({
                id: 'account.email_pages.signals_tab_title',
                defaultMessage: 'Signals',
              }),
        icon: <Icon icon={faSignal} size="1x" />,
        content: (
          <EmailSignalsSidebar
            timeline={emailTimeline}
            isLoading={isSignalsLoading}
          />
        ),
      },
    ],
    [emailTimeline, isSignalsLoading, signalCount, intl],
  );

  useEffect(() => {
    // Only auto-select when no thread is selected yet — never navigate on
    // filter changes while a thread is already open.
    if (isFetching || isLoading || isThreadSelected) {
      return;
    }
    const firstThread = emails[0];

    if (firstThread) {
      navigateToEmail(
        projectId ?? company.companyId,
        firstThread.emailThreadId,
      );
    }
  }, [
    isFetching,
    isLoading,
    isThreadSelected,
    emails,
    navigateToEmail,
    projectId,
    company.companyId,
  ]);

  const sidebarItems = useMemo(
    (): ItemType[] =>
      emails.map((email) => ({
        label: (
          <EmailListItemContent>
            <EmailListItemHeader>
              <EmailListItemTitle>
                {email.subject ||
                  intl.formatMessage({
                    id: 'account.email_pages.no_subject',
                    defaultMessage: '(No subject)',
                  })}
              </EmailListItemTitle>
              <EmailListItemDate>
                {email.latestMessageTimestamp || email.updatedAt
                  ? formatShortDateInTimezone(
                      email.latestMessageTimestamp ?? email.updatedAt,
                      userTimeZone,
                    )
                  : ''}
              </EmailListItemDate>
            </EmailListItemHeader>
            {email.latestMessagePreview && (
              <EmailListItemPreview>
                {email.latestMessagePreview}
              </EmailListItemPreview>
            )}
          </EmailListItemContent>
        ),
        key: String(email.emailThreadId),
        icon: <FontAwesomeIcon icon={faEnvelopeRegular} size={12} />,
      })),
    [emails, intl, userTimeZone],
  );

  const handleSidebarSelect = useCallback(
    (key: string) => {
      navigateToEmail(projectId ?? company.companyId, Number(key));
    },
    [company.companyId, projectId, navigateToEmail],
  );

  const handleListScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      const el = event.currentTarget;
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

      if (distanceToBottom < INFINITE_SCROLL_THRESHOLD) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // After each page completes, check whether the loaded threads fill the
  // sidebar's visible height. If there's still empty space and more pages
  // exist, fetch the next page automatically — without waiting for the
  // user to scroll.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    const el = sidebarRef.current;

    if (!el) {
      return;
    }
    if (el.scrollHeight <= el.clientHeight) {
      fetchNextPage();
    }
  }, [emails.length, isFetchingNextPage, hasNextPage, fetchNextPage]);

  const filterEmptyState = useMemo(
    () => (
      <RocketlaneEmptyStateRoot>
        <RocketlaneEmptyState
          icon={{ icon: faEnvelope }}
          message={intl.formatMessage({
            id: 'email_page.filter_empty_state_title',
            defaultMessage: 'No emails match your filters',
          })}
          description={intl.formatMessage({
            id: 'email_page.filter_empty_state_message',
            defaultMessage: 'Try adjusting your filters, or clear them',
          })}
          primaryAction={{
            onClick: () => {
              onClearFilter?.();
              onSearchTextChange?.('');
            },
            children: intl.formatMessage({
              id: 'email_page.filter_empty_state_cta',
              defaultMessage: 'Clear filter',
            }),
          }}
        />
      </RocketlaneEmptyStateRoot>
    ),
    [intl, onClearFilter, onSearchTextChange],
  );

  const searchPlaceholder = useTranslation('search');
  const SideBar = useMemo(
    () => (
      <SidebarContentLayout>
        <SidebarSearchSection>
          <SidebarSearchRow>
            <SearchInputWrapper>
              <SearchInputExpanded
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={(e) => onSearchTextChange?.(e.target.value)}
                onClear={() => onSearchTextChange?.('')}
              />
            </SearchInputWrapper>
            {hostApi && (
              <FilterLabelWrapper>
                {hostApi.renderComponent({
                  type: 'email_inbox_filter',
                  props: { signalFields, emailFields },
                })}
              </FilterLabelWrapper>
            )}
          </SidebarSearchRow>
        </SidebarSearchSection>
        {isLoading || (isFetching && !isFetchingNextPage) ? (
          <SidebarEmailList>
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          </SidebarEmailList>
        ) : emails.length === 0 &&
          (filterPayload != null || searchText !== '') ? (
          filterEmptyState
        ) : (
          <SidebarEmailList ref={sidebarRef} onScroll={handleListScroll}>
            <EmailSidebarMenu
              items={sidebarItems}
              onSelect={({ key }) => handleSidebarSelect(String(key))}
              selectedKeys={[selectedAssociationKey]}
              mode="inline"
            />
          </SidebarEmailList>
        )}
      </SidebarContentLayout>
    ),
    [
      filterEmptyState,
      searchPlaceholder,
      searchText,
      onSearchTextChange,
      isLoading,
      isFetching,
      isFetchingNextPage,
      emails.length,
      filterPayload,
      sidebarItems,
      selectedAssociationKey,
      handleSidebarSelect,
      hostApi,
      signalFields,
      emailFields,
      sidebarRef,
      handleListScroll,
    ],
  );

  if (isLoading && !initialLoadComplete.current) {
    return <PageLoading />;
  }

  return (
    <PageWrapper>
      {isSidebarOpen && <EmailV2Sidebar>{SideBar}</EmailV2Sidebar>}

      <PageContent>
        <MainContent>
          <EmailFilterBarLayout>
            <FilterBar>
              <FilterBar.Primary>
                {emails.length === 0 && filterPayload == null ? null : (
                  <Popover
                    content={
                      isSidebarOpen ? null : (
                        <EmailV2SidebarInPopover>
                          {SideBar}
                        </EmailV2SidebarInPopover>
                      )
                    }
                    placement="bottomLeft"
                    destroyTooltipOnHide
                    isDisabled={isSidebarOpen || activeRightSidebarKey == null}
                    open={!isSidebarOpen && isListPopoverOpen}
                    onOpenChange={(open) => {
                      if (!isSidebarOpen) {
                        setIsListPopoverOpen(open);
                      }
                    }}
                    trigger="click"
                  >
                    <Button
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                      onClick={() => {
                        if (activeRightSidebarKey == null) {
                          setIsSidebarOpen(!isSidebarOpen);
                        }
                      }}
                    />
                  </Popover>
                )}
                {isThreadSelected && !isThreadNotFound && (
                  <EmailActionBarTitleWrapper>
                    <EmailActionBarTitleInner
                      $isPlaceholder={isPlaceholderSubject}
                      data-cy="action-bar-subject"
                    >
                      {displaySubject || noSubjectLabel}
                    </EmailActionBarTitleInner>
                  </EmailActionBarTitleWrapper>
                )}
              </FilterBar.Primary>
              {sourceIdForTranscript != null && !isThreadNotFound && (
                <FilterBar.Secondary>
                  <FilterBarActions>
                    <Tooltip
                      title={
                        isAllExpanded
                          ? intl.formatMessage({
                              id: 'account.email_pages.collapse_all',
                              defaultMessage: 'Collapse all',
                            })
                          : intl.formatMessage({
                              id: 'account.email_pages.expand_all',
                              defaultMessage: 'Expand all',
                            })
                      }
                      placement="bottom"
                    >
                      <Button
                        variant="ghost"
                        leftIcon={
                          <FontAwesomeIcon
                            icon={
                              isAllExpanded
                                ? faArrowsToDottedLine
                                : faAnglesUpDown
                            }
                            size={12}
                          />
                        }
                        onClick={() => {
                          if (isAllExpanded) {
                            transcriptRef.current?.collapseAll();
                            setIsAllExpanded(false);
                          } else {
                            transcriptRef.current?.expandAll();
                            setIsAllExpanded(true);
                          }
                        }}
                      />
                    </Tooltip>
                    {!isSignalsLoading && signalCount > 0 && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          if (
                            activeRightSidebarKey ===
                            EmailPagesRightSidebarKeys.Signals
                          ) {
                            handleCloseRightSidebar();
                          } else {
                            openRightSideBar(
                              EmailPagesRightSidebarKeys.Signals,
                            );
                          }
                        }}
                      >
                        {activeRightSidebarKey ===
                        EmailPagesRightSidebarKeys.Signals
                          ? intl.formatMessage({
                              id: 'account.email_pages.hide_signals',
                              defaultMessage: 'Hide signals',
                            })
                          : intl.formatMessage(
                              {
                                id: 'account.email_pages.show_signals',
                                defaultMessage: 'Show signals ({count})',
                              },
                              { count: signalCount },
                            )}
                      </Button>
                    )}
                    {(!isModuleFed ||
                      emailPermissions?.canDeleteEmailPages) && (
                      <MoreActionsDropdown
                        menu={{
                          onClick: ({ key }) => {
                            if (key === 'delete') {
                              confirmDeleteThread();
                            }
                          },
                          items: [
                            {
                              key: 'delete',
                              label: intl.formatMessage({
                                id: 'account.email_pages.delete_thread',
                                defaultMessage: 'Delete email thread',
                              }),
                              icon: <FontAwesomeIcon icon={faTrash} />,
                              danger: true,
                            },
                          ],
                        }}
                        triggerElement={
                          <Button
                            variant="ghost"
                            leftIcon={
                              <FontAwesomeIcon
                                icon={faEllipsisVertical}
                                size={12}
                              />
                            }
                          />
                        }
                      />
                    )}
                  </FilterBarActions>
                </FilterBar.Secondary>
              )}
            </FilterBar>
          </EmailFilterBarLayout>

          {isLoading && !initialLoadComplete.current ? (
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          ) : emails.length === 0 &&
            !isFetching &&
            !isThreadSelected &&
            (filterPayload != null || searchText !== '') ? (
            filterEmptyState
          ) : emails.length === 0 && !isFetching && !isThreadSelected ? (
            <RocketlaneEmptyStateRoot>
              <RocketlaneEmptyState
                icon={{ icon: faEnvelope }}
                message={intl.formatMessage({
                  id: 'email_page.empty_state_title',
                })}
                description={intl.formatMessage({
                  id: 'email_page.empty_state_message',
                })}
              />
            </RocketlaneEmptyStateRoot>
          ) : isThreadNotFound ? (
            <EmailThreadNotFoundState />
          ) : (
            <TabContent>
              {sourceIdForTranscript != null && !isTranscriptReady && (
                <ContentReadyOverlay>
                  <Loading size="large" />
                </ContentReadyOverlay>
              )}
              {sourceIdForTranscript != null ? (
                <HiddenUntilReady $hidden={!isTranscriptReady}>
                  <EmailTranscript
                    ref={transcriptRef}
                    sourceId={sourceIdForTranscript}
                    renderAttachment={renderAttachment}
                    emailMessageId={emailMessageId}
                    onIframeClick={() => setIsListPopoverOpen(false)}
                    onExpandedContentReady={() =>
                      setIsTranscriptContentReady(true)
                    }
                    header={
                      isBlockedThread || showSummaryBanner ? (
                        <BannerCenterWrapper>
                          <BannerCenterInner>
                            {isBlockedThread && <BlockedContactBanner />}
                            {showSummaryBanner && (
                              <EmailSummaryCard
                                key={sourceIdForTranscript}
                                data-cy="summary-card"
                                summaryPoints={summaryPoints}
                              />
                            )}
                          </BannerCenterInner>
                        </BannerCenterWrapper>
                      ) : undefined
                    }
                  />
                </HiddenUntilReady>
              ) : (
                <LoadingWrapper>
                  <Loading size="large" />
                </LoadingWrapper>
              )}
            </TabContent>
          )}
        </MainContent>
      </PageContent>

      <MeetingPagesSidePane
        items={rightSidebarItems}
        activeKey={activeRightSidebarKey}
        onSelect={(key) => {
          if (key === activeRightSidebarKey) {
            handleCloseRightSidebar();
          } else {
            openRightSideBar(key as EmailPagesRightSidebarKeys);
          }
        }}
        onClose={handleCloseRightSidebar}
      />
      {deleteConfirmationDialog}
    </PageWrapper>
  );
};

const AccountEmailsContent = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed,
  renderAttachment,
  hostApi,
  filterPayload,
  onClearFilter,
  emailFields,
  emailMessageId,
}: AccountEmailsProps) => {
  return (hostApi?.hasFeatures?.('emailPagesV2') ?? false) ? (
    <AccountEmailsContentV2
      company={company}
      projectId={projectId}
      emailPermissions={emailPermissions}
      isModuleFed={isModuleFed}
      renderAttachment={renderAttachment}
      hostApi={hostApi}
      filterPayload={filterPayload}
      onClearFilter={onClearFilter}
      emailFields={emailFields}
      emailMessageId={emailMessageId}
    />
  ) : (
    <AccountEmailsContentV1
      company={company}
      projectId={projectId}
      emailPermissions={emailPermissions}
      isModuleFed={isModuleFed}
      renderAttachment={renderAttachment}
    />
  );
};

export const AccountEmails = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
  renderAttachment,
  hostApi,
  filterPayload,
  onClearFilter,
  emailFields,
  emailMessageId,
}: AccountEmailsProps) => {
  return (
    <ChatProvider>
      <AccountEmailsContent
        company={company}
        projectId={projectId}
        emailPermissions={emailPermissions}
        isModuleFed={isModuleFed}
        renderAttachment={renderAttachment}
        hostApi={hostApi}
        filterPayload={filterPayload}
        onClearFilter={onClearFilter}
        emailFields={emailFields}
        emailMessageId={emailMessageId}
      />
    </ChatProvider>
  );
};
